import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { SplitWords } from '@/components/generals/split-words'
import { AccordionList } from '@/components/generals/accordion-list'
import { SupportForm } from '@/components/support/support-form'
import { useGsap } from '@/hooks/use-gsap'
import { supportFaqGroups, legalCards } from '@/constants/support'
import { siteConfig } from '@/constants/site'

const Support: FC = (): ReactNode => {
    const scope = useGsap<HTMLDivElement>(({ gsap, select, reduced }) => {
        if (reduced) return

        const intro = gsap.timeline({ defaults: { ease: 'nuvea' } })

        intro
            .from(select('[data-support-title] .reveal-mask > span'), {
                yPercent: 115,
                duration: 1.15,
                stagger: 0.06
            })
            .from(
                select('[data-support-detail]'),
                { y: 24, opacity: 0, duration: 0.85, stagger: 0.08 },
                '-=0.75'
            )
            .from(
                select('[data-support-form]'),
                { y: 34, opacity: 0, duration: 0.95 },
                '-=0.7'
            )

        // Each FAQ group announces itself, then its rows peel in.
        select('[data-faq-group]').forEach((group) => {
            const trigger = { trigger: group, start: 'top 80%' }

            gsap.from(group.querySelectorAll('[data-faq-group-title] .reveal-mask > span'), {
                yPercent: 115,
                duration: 1,
                stagger: 0.05,
                ease: 'nuvea',
                scrollTrigger: trigger
            })

            gsap.from(group.querySelectorAll('[data-accordion-row]'), {
                y: 24,
                opacity: 0,
                duration: 0.75,
                stagger: 0.055,
                ease: 'nuvea',
                scrollTrigger: { trigger: group, start: 'top 76%' }
            })
        })

        gsap.from(select('[data-legal-card]'), {
            y: 44,
            opacity: 0,
            duration: 0.95,
            stagger: 0.1,
            ease: 'nuvea',
            scrollTrigger: {
                trigger: select('[data-legal-grid]')[0],
                start: 'top 88%'
            }
        })
    })

    return (
        <Layout>
            <Seo
                title='Support'
                description='Questions about an order, the formula, shipping or returns. Write to us and a person
                answers, usually within one working day.'
            />

            <div ref={scope}>
                <section
                    id='support-intro'
                    className='shell-x mx-auto w-full max-w-[1760px] pt-10 md:pt-16'
                >
                    <SplitWords
                        data-support-title=''
                        as='h1'
                        lines={['Support']}
                        className='type-display text-ink'
                    />

                    <div className='mt-12 grid grid-cols-1 gap-12 md:mt-16 lg:grid-cols-2 lg:gap-16'>
                        <div className='flex flex-col gap-10'>
                            <p data-support-detail='' className='type-lead max-w-[38ch] text-ink'>
                                <strong className='font-semibold'>
                                    Something you need a person for?
                                </strong>{' '}
                                <span className='text-slate-soft'>
                                    Write to us directly or use the form. Both reach the same two people.
                                </span>
                            </p>

                            <div data-support-detail=''>
                                <h2 className='type-h3 text-ink'>
                                    Where we are
                                </h2>

                                <p className='type-copy mt-2 text-slate-soft'>
                                    {siteConfig.address}
                                </p>
                            </div>

                            <div className='grid grid-cols-1 gap-8 sm:grid-cols-2'>
                                <div data-support-detail=''>
                                    <h2 className='type-h3 text-ink'>
                                        Email
                                    </h2>

                                    <a
                                        href={`mailto:${siteConfig.email}`}
                                        className='type-copy link-wipe mt-2 inline-block text-slate-soft'
                                    >
                                        {siteConfig.email}
                                    </a>
                                </div>

                                <div data-support-detail=''>
                                    <h2 className='type-h3 text-ink'>
                                        Phone
                                    </h2>

                                    <a
                                        href={siteConfig.phoneHref}
                                        className='type-copy link-wipe mt-2 inline-block text-slate-soft'
                                    >
                                        {siteConfig.phone}
                                    </a>
                                </div>
                            </div>
                        </div>

                        <SupportForm />
                    </div>
                </section>

                {supportFaqGroups.map((group) => (
                    <section
                        key={group.id}
                        id={group.id}
                        data-faq-group=''
                        className='shell-x mx-auto w-full max-w-[1760px] pt-24 md:pt-32'
                    >
                        <div className='grid grid-cols-1 gap-8 lg:grid-cols-[1fr_1.3fr] lg:gap-16'>
                            <SplitWords
                                data-faq-group-title=''
                                as='h2'
                                lines={group.titleLines}
                                className='type-h1 max-w-[12ch] text-ink lg:sticky lg:top-28 lg:self-start'
                            />

                            <div>
                                <AccordionList items={group.items} />
                            </div>
                        </div>
                    </section>
                ))}

                <section
                    id='payment-legal'
                    className='shell-x mx-auto w-full max-w-[1760px] pt-24 pb-24 md:pt-32 md:pb-32'
                >
                    <h2 className='type-h1 text-center text-ink'>
                        Payment &amp; legal
                    </h2>

                    <ul
                        data-legal-grid=''
                        className='mt-12 grid grid-cols-1 gap-6 md:mt-16 md:grid-cols-3'
                    >
                        {legalCards.map((card) => (
                            <li key={card.href} data-legal-card=''>
                                <Link
                                    href={card.href}
                                    className='group flex h-full flex-col justify-between gap-10 rounded-2xl border border-stone-line bg-shell p-7 transition-[background-color,border-color,transform] duration-500 ease-nuvea hover:-translate-y-1 hover:border-ink/25 hover:bg-cream md:p-9'
                                >
                                    <span className='flex size-10 items-center justify-center rounded-full border border-stone-line text-ink transition-[background-color,border-color,color] duration-400 ease-nuvea group-hover:border-ink group-hover:bg-ink group-hover:text-cream'>
                                        <ArrowUpRight className='size-4' strokeWidth={1.6} />
                                    </span>

                                    <span>
                                        <span className='type-h3 block text-ink'>
                                            {card.title}
                                        </span>

                                        <span className='type-copy mt-3 block max-w-[34ch] text-slate-soft'>
                                            {card.body}
                                        </span>
                                    </span>
                                </Link>
                            </li>
                        ))}
                    </ul>
                </section>
            </div>
        </Layout>
    )
}

export default Support
